import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  Typography,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { FC, useState } from "react";
import {
  IContent,
  IContentTypes,
} from "../../../models/admin/cms/contentTypes";
import CustomDialog from "../../../common/CustomDialog/CustomDialog";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { VisuallyHiddenInput } from "../CreateContent/CreateContentUtils";
import RichTextEditor from "../../components/RichTextEditor/RichTextEditor";
import { useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";

interface IContentTypeList {
  contentTypes: IContentTypes[];
  content: IContent[];
}

const ContentTypeList: FC<IContentTypeList> = ({ contentTypes, content }) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [previewTitle, setPreviewTitle] = useState<string>("");
  const { isLoading, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );

  if (!contentTypes.length)
    return (
      <Typography 
        variant="h5" 
        sx={{ textAlign: "start" }}
        id="content_type_list_empty"
        data-test-id="content_type_list_empty"
        aria-label="No Content Types Available"
      >
        No Content Types yet. Create one!
      </Typography>
    );

  const isContentTypeExists = (contentType: string): boolean => {
    if (!content || content.length === 0) return false;
    return content.some((item) => item.contentTypeName === contentType);
  };

  const handlePreview = async (id: string) => {
    const selectedCard = contentTypes?.filter((item) => item?.id === id);
    setPreviewModalOpen(true);
    setPreviewTitle(selectedCard[0]?.contentTypeName);
  };

  const getContentTypeInfo = (title: string): React.ReactNode => {
    const selectedCard = contentTypes?.filter(
      (item) => item?.contentTypeName?.toLowerCase() === title?.toLowerCase()
    );

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
        }}
        id="content_type_preview_content"
        data-test-id="content_type_preview_content"
        aria-label="Content Type Preview Content"
      >
        {selectedCard[0]?.fields?.map((field) => {
          if (field?.fieldType === "text" || field?.fieldType === "link") {
            return (
              <TextField
                key={field?.fieldLabel}
                label={field?.fieldLabel}
                required={field?.required}
                disabled
                id={`content_type_preview_text_${field?.fieldLabel}`}
                data-test-id={`content_type_preview_text_${field?.fieldLabel}`}
                aria-label={`Content Type Preview Text: ${field?.fieldLabel}`}
              />
            );
          } else if (field?.fieldType === "image") {
            return (
              <Button
                key={field?.fieldLabel}
                component="label"
                role={undefined}
                variant="contained"
                tabIndex={-1}
                startIcon={<CloudUploadIcon />}
                sx={{ borderRadius: "4px" }}
                id={`content_type_preview_image_${field?.fieldLabel}`}
                data-test-id={`content_type_preview_image_${field?.fieldLabel}`}
                aria-label={`Content Type Preview Image: ${field?.fieldLabel}`}
                disabled
              >
                {field?.fieldLabel}
                {field?.required && "*"}
                <VisuallyHiddenInput
                  type="file"
                  required={field?.required}
                  disabled
                />
              </Button>
            );
          } else if (field?.fieldType === "multiline") {
            return <RichTextEditor isDisabled={true} key={field?.fieldLabel} />;
          } else if (field?.fieldType === "color") {
            return (
              <TextField
                key={field?.fieldLabel}
                sx={{ width: "220px" }}
                type="color"
                label={field?.fieldLabel}
                defaultValue="#ff0000"
                required={field?.required}
                disabled
                id={`content_type_preview_color_${field?.fieldLabel}`}
                data-test-id={`content_type_preview_color_${field?.fieldLabel}`}
                aria-label={`Content Type Preview Color: ${field?.fieldLabel}`}
              />
            );
          }
          return null;
        })}
      </Box>
    );
  };

  const hadlePreviewDialogClose = () => {
    setPreviewModalOpen(false);
    setPreviewTitle("");
  };

  return (
    <>
      <Box 
        sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}
        id="content_type_list_container"
        data-test-id="content_type_list_container"
        aria-label="Content Type List"
      >
        {isLoading && calledReducerType === "cms/getContentTypes"
          ? Array.from(Array(4)).map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                width="270px"
                height={150}
                animation="wave"
                id={`content_type_list_skeleton_${index}`}
                data-test-id={`content_type_list_skeleton_${index}`}
                aria-label="Loading Content Type"
              />
            ))
          : contentTypes?.map((type, index) => (
              <Card
                key={index}
                sx={{ minWidth: 275, width: isMobile ? "100%" : "auto" }}
                id={`content_type_card_${index}`}
                data-test-id={`content_type_card_${index}`}
                aria-label={`Content Type: ${type?.contentTypeName}`}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontSize: "0.9rem" }}
                    id={`content_type_name_${index}`}
                    data-test-id={`content_type_name_${index}`}
                    aria-label={`Content Type Name: ${type?.contentTypeName}`}
                  >
                    {type?.contentTypeName}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: "0.9rem", fontStyle: "italic" }}
                    id={`content_type_fields_count_${index}`}
                    data-test-id={`content_type_fields_count_${index}`}
                    aria-label={`${type?.fields?.length} fields defined`}
                  >
                    {`${type?.fields?.length} fields defined`}
                  </Typography>
                  <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handlePreview(type?.id)}
                      id={`content_type_preview_${index}`}
                      data-test-id={`content_type_preview_${index}`}
                      aria-label={`Preview ${type?.contentTypeName}`}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      // disabled={isContentTypeExists(type?.contentTypeName)}
                      disabled={true}
                      id={`content_type_edit_${index}`}
                      data-test-id={`content_type_edit_${index}`}
                      aria-label={`Edit ${type?.contentTypeName}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      // disabled={isContentTypeExists(type?.contentTypeName)}
                      disabled={true}
                      id={`content_type_delete_${index}`}
                      data-test-id={`content_type_delete_${index}`}
                      aria-label={`Delete ${type?.contentTypeName}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
      </Box>
      <CustomDialog
        open={previewModalOpen}
        onClose={hadlePreviewDialogClose}
        maxWidth="md"
        title={previewTitle}
        content={getContentTypeInfo(previewTitle)}
        showDivider={true}
      />
    </>
  );
};

export default ContentTypeList;
